import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import NewsletterSignup from '../components/NewsletterSignup';
import PartnersStrip from '../components/PartnersStrip';
import { trackEvent } from '../utils/analytics';
import { ANALYTICS_EVENTS } from '../utils/analyticsEvents';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '../utils/appLinks';
import { BRAND_TAGLINE, DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL } from '../utils/seo';
import { getAllPosts } from '../components/blogUtils';

export default function Home() {
  const [latestPosts, setLatestPosts] = useState([]);

  useEffect(() => {
    getAllPosts()
      .then(posts => setLatestPosts(posts.slice(0, 3)))
      .catch(() => setLatestPosts([]));
  }, []);

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": SITE_NAME,
    "description": "Integration guidance, cultural orientation, and everyday life support in Germany for African families.",
    "url": SITE_URL,
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/images/sistakyerewaa-logo.svg`,
      },
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${SITE_URL}/store?search={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <Helmet>
        <title>{BRAND_TAGLINE}</title>
        <meta
          name="description"
          content="Sista Kyerewaa shares integration guidance, cultural context, wellness education, and practical life-in-Germany support for African families."
        />
        <meta
          name="keywords"
          content="Ghanaian in Germany, African lifestyle creator Germany, nurse health tips Germany, vegan African recipes, plant-based African food, immigrant life in Germany tips, integration advice Germany, Ghanaian community Germany, African family wellness, life abroad inspiration"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:title" content={BRAND_TAGLINE} />
        <meta
          property="og:description"
          content="Integration guidance, cultural context, wellness education, and practical life-in-Germany support for African families."
        />
        <meta property="og:image" content={DEFAULT_OG_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={SITE_URL} />
        <meta property="twitter:title" content={BRAND_TAGLINE} />
        <meta
          property="twitter:description"
          content="Integration guidance, cultural context, wellness education, and practical life-in-Germany support for African families."
        />
        <meta property="twitter:image" content={DEFAULT_OG_IMAGE} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <link rel="canonical" href={SITE_URL} />
      </Helmet>

      <main className="min-h-screen">
        <section className="pt-20 pb-16 px-6">
          <div className="max-w-6xl mx-auto grid gap-12 md:grid-cols-[1.1fr_0.9fr] items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Community • Culture • Confidence</p>
              <h1 className="font-display text-4xl md:text-6xl leading-tight mt-6 text-[#1A1A1A]">
                Life in Germany made easier for African families.
              </h1>
              <p className="text-lg md:text-xl text-[#4A4541] mt-6 max-w-3xl">
                Trusted by thousands online, Sista Kyerewaa empowers African families with health education,
                real‑life integration tips, and vegan African recipes — all delivered with warmth, faith, and authenticity.
              </p>
              <p className="text-sm uppercase tracking-[0.3em] text-[#8C7B6B] mt-4">
                Welcome to the Sista Kyerewaa community
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-10">
                <a
                  href="https://www.tiktok.com/@sistakyerewaa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-3 bg-[#2E4036] text-[#F2F0E9] text-sm uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors"
                >
                  Follow on TikTok
                </a>
                <a
                  href="/book"
                  className="inline-flex items-center justify-center px-8 py-3 border border-[#8C7B6B] text-sm uppercase tracking-[0.25em] text-[#2E4036] hover:bg-[#ede9df] transition-colors"
                >
                  Work With Me
                </a>
              </div>
              <a
                href="https://linktr.ee/sistakyerewaa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex mt-4 text-xs uppercase tracking-[0.25em] text-[#8C7B6B] hover:text-[#2E4036] transition-colors"
              >
                Explore Linktree
              </a>
            </div>
            <div className="flex flex-col items-center md:items-end">
              <div className="border border-[#8C7B6B]/20 bg-white/70 p-4 shadow-sm">
                <img
                  src="/images/sistakyerewaa-hero.png"
                  alt="Sista Kyerewaa, community educator and registered nurse helping African families thrive in Germany"
                  className="w-full max-w-sm object-cover rounded-none"
                  loading="eager"
                />
              </div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#8C7B6B] mt-4">sistakyerewaa</p>
            </div>
          </div>
        </section>

        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto grid gap-6 md:grid-cols-3">
            {[
              {
                title: 'Community Education',
                copy: 'Practical guidance for African and Ghanaian families navigating life in Germany.',
                tag: 'Integration',
              },
              {
                title: 'Integration & Systems',
                copy: 'Clear guidance for paperwork, daily systems, and confident integration.',
                tag: 'Life in Germany',
              },
              {
                title: 'Lifestyle & Living',
                copy: 'Everyday tips for housing, work, family routines, and thriving abroad.',
                tag: 'Lifestyle & Living',
              },
            ].map((item) => (
              <Link
                key={item.title}
                to={`/blog?tag=${encodeURIComponent(item.tag)}`}
                className="border border-[#8C7B6B]/20 bg-white/70 p-6 shadow-sm hover:border-[#8C7B6B] hover:shadow-md transition-[border-color,box-shadow] duration-200"
              >
                <h3 className="font-display text-2xl text-[#2E4036] mb-3">{item.title}</h3>
                <p className="text-[#4A4541]">{item.copy}</p>
                <p className="text-xs uppercase tracking-[0.3em] text-[#8C7B6B] mt-4">Read the blog</p>
              </Link>
            ))}
          </div>
        </section>

        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto border border-[#8C7B6B]/20 bg-white/70 p-8 md:p-10">
            <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">New Platform</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-4">SistaKyerewaa Language App is live</h2>
            <p className="text-[#4A4541] mt-4 max-w-3xl">
              Follow a Ghana or Germany learning path with AI coaching and cultural guidance built for diaspora integration.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get SistaKyerewaa on Google Play"
                onClick={() => trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, { location: 'home_google_play', outbound: true })}
                className="inline-flex min-w-[172px] items-center gap-3 rounded-lg bg-[#1c1b1a] px-4 py-2.5 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <svg viewBox="0 0 512 512" className="h-8 w-8 shrink-0" aria-hidden="true">
                  <path fill="#00D2FF" d="M48 32 301 256 48 480Z" />
                  <path fill="#00E676" d="m48 32 319 184-66 40Z" />
                  <path fill="#FFEA00" d="m48 480 319-184-66-40Z" />
                  <path fill="#FF3D71" d="m367 216 89 51c11 6 11 22 0 28l-89 51-66-40Z" />
                </svg>
                <span className="leading-tight text-left">
                  <span className="block text-[10px] uppercase tracking-[0.15em]">Get it on</span>
                  <span className="block text-base font-medium">Google Play</span>
                </span>
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download SistaKyerewaa on the App Store"
                onClick={() => trackEvent(ANALYTICS_EVENTS.APP_OPEN_CLICK, { location: 'home_app_store', outbound: true })}
                className="inline-flex min-w-[172px] items-center gap-3 rounded-lg bg-[#1c1b1a] px-4 py-2.5 text-white shadow-sm transition-transform hover:-translate-y-0.5"
              >
                <svg viewBox="0 0 384 512" className="h-8 w-8 shrink-0 fill-current" aria-hidden="true">
                  <path d="M318.7 268.7c-.2-36.7 16.4-64.3 49.8-84.7-18.7-26.8-47-41.5-84.2-44.4-35.2-2.8-73.7 20.5-87.8 20.5-14.9 0-49-19.5-75.8-19.5C65.3 141.5 6.3 184.8 6.3 273c0 26.1 4.8 53 14.4 80.7 12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.9-17.9 31.9 0 48.3 17.9 76.5 17.9 48.6-.7 90.4-82.5 102.6-119.3-67.7-31.9-64.2-89-64.2-90.9zM262.6 105.1c27.1-32.2 24.6-61.5 23.8-72.1-23.9 1.4-51.6 16.3-67.4 34.7-17.4 19.7-27.6 44-25.4 71.5 25.8 2 49.6-11.2 69-34.1z" />
                </svg>
                <span className="leading-tight text-left">
                  <span className="block text-[10px] tracking-[0.06em]">Download on the</span>
                  <span className="block text-base font-medium">App Store</span>
                </span>
              </a>
            </div>
          </div>
        </section>

        <PartnersStrip />

        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">As Seen On</p>
                <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-3">TikTok highlights</h2>
              </div>
              <a
                href="https://www.tiktok.com/@sistakyerewaa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm uppercase tracking-[0.25em] text-[#8C7B6B] hover:text-[#2E4036]"
              >
                Watch more
              </a>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  title: 'Integration & systems',
                  copy: 'Practical tips for navigating daily life, culture, and paperwork in Germany.',
                },
                {
                  title: 'Germany lifestyle',
                  copy: 'Work, housing, family routines, and thriving abroad with confidence.',
                },
                {
                  title: 'Community support',
                  copy: 'Stories and guidance that make settling in feel less overwhelming.',
                },
              ].map((item) => (
                <a
                  key={item.title}
                  href="https://www.tiktok.com/@sistakyerewaa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#8C7B6B]/20 bg-white/70 p-6 hover:border-[#8C7B6B] hover:shadow-md transition-[border-color,box-shadow] duration-200"
                >
                  <h3 className="font-display text-2xl text-[#2E4036] mb-3">{item.title}</h3>
                  <p className="text-[#4A4541]">{item.copy}</p>
                  <p className="text-xs uppercase tracking-[0.3em] text-[#8C7B6B] mt-4">Watch on TikTok</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto border border-[#8C7B6B]/20 bg-[#2E4036] p-8 text-center">
            <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Core Values</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#F2F0E9] mt-4">What I Believe In</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 text-[#F2F0E9]/80">
              <p>Community before competition</p>
              <p>Health is wealth</p>
              <p>Faith guides everything</p>
              <p>Culture is strength</p>
              <p>Knowledge should be simple and accessible</p>
              <p>We rise by lifting others</p>
            </div>
          </div>
        </section>

        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">From the blog</p>
                <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-3">Latest guides</h2>
              </div>
              <Link to="/blog" className="text-sm uppercase tracking-[0.25em] text-[#8C7B6B] hover:text-[#2E4036]">
                Read all
              </Link>
            </div>

            {latestPosts.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {latestPosts.map(post => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="border border-[#8C7B6B]/20 bg-white/70 hover:shadow-md transition-shadow duration-200 group"
                  >
                    {post.cover && (
                      <img
                        src={post.cover}
                        alt={post.title}
                        className="w-full h-48 object-cover"
                        loading="lazy"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="p-6">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.tags && post.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-1 text-[0.65rem] uppercase tracking-[0.2em] bg-[#e7e4df] text-[#2d2b29]">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="font-display text-xl text-[#2E4036] group-hover:text-[#1a2a20] transition-colors mb-2">
                        {post.title}
                      </h3>
                      <p className="text-sm text-[#4A4541] line-clamp-3">{post.excerpt}</p>
                      <p className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B] mt-4">Read more</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {[
                  { title: 'Integration & Systems', copy: 'Practical guides for navigating daily life in Germany.', tag: 'Integration' },
                  { title: 'Health & Wellness', copy: 'Nurse-led health guidance for African families in Germany.', tag: 'Life in Germany' },
                  { title: 'Veggie Kitchen', copy: 'Plant-based African recipes for the diaspora kitchen.', tag: 'Lifestyle & Living' },
                ].map(item => (
                  <Link
                    key={item.title}
                    to={`/blog?tag=${encodeURIComponent(item.tag)}`}
                    className="border border-[#8C7B6B]/20 bg-white/70 p-6 hover:shadow-md transition-shadow duration-200 group"
                  >
                    <h3 className="font-display text-xl text-[#2E4036] group-hover:text-[#1a2a20] transition-colors mb-2">{item.title}</h3>
                    <p className="text-sm text-[#4A4541]">{item.copy}</p>
                    <p className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B] mt-4">Explore posts</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto">
            <NewsletterSignup
              title="Get the Germany Starter Checklist"
              subtitle="A short, practical checklist for Africans moving to or living in Germany — delivered straight to your inbox."
              benefits={[
                { title: 'Clear steps', description: 'What to do first, and why it matters.' },
                { title: 'Health basics', description: 'Insurance, appointments, and care tips.' },
                { title: 'Community tips', description: 'How to settle in and find support.' },
              ]}
              successTitle="You’re in."
              successBody="Your Germany Starter Checklist will arrive in your email shortly. You can also open it here."
              ctaLabel="Send me the checklist"
              source="home-checklist"
              tag="checklist"
              successLinkLabel="Open checklist"
              successLinkHref="/checklist"
              onSuccess={(result) => {
                if (typeof window !== 'undefined') {
                  if (result?.token) {
                    window.localStorage.setItem('checklistToken', result.token);
                  }
                  window.localStorage.setItem('checklistAccess', 'granted');
                  if (result?.token) {
                    window.location.assign(`/checklist?token=${encodeURIComponent(result.token)}`);
                  }
                }
              }}
            />
          </div>
        </section>

        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto border border-[#8C7B6B]/20 bg-[#F2F0E9] p-10 text-center">
            <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Let’s Grow Together</p>
            <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-4">
              Whether you’re new to Germany, improving your health, or looking for a positive community, you’re welcome here.
            </h2>
            <p className="text-[#4A4541] mt-4">
              More than a platform, this is a family. If my story or experience can help you even a little, then this space has done its job.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a
                href="https://www.tiktok.com/@sistakyerewaa"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-3 bg-[#2E4036] text-[#F2F0E9] text-sm uppercase tracking-[0.25em] hover:bg-[#1a2a20] transition-colors"
              >
                Follow on TikTok
              </a>
              <a
                href="/book"
                className="inline-flex items-center justify-center px-8 py-3 border border-[#8C7B6B] text-sm uppercase tracking-[0.25em] text-[#2E4036] hover:bg-[#ede9df] transition-colors"
              >
                Book a Session
              </a>
            </div>
            <a
              href="https://linktr.ee/sistakyerewaa"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex mt-4 text-xs uppercase tracking-[0.25em] text-[#8C7B6B] hover:text-[#2E4036] transition-colors"
            >
              Explore Linktree
            </a>
          </div>
        </section>

        <section className="px-6 pb-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-6 mb-8">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-[#CC5833]">Services</p>
                <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mt-3">Ways to work together</h2>
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {[
                {
                  title: '1‑on‑1 Consultation',
                  copy: 'Personal guidance for life in Germany, wellness, and next steps.',
                  price: 'Request pricing',
                  cta: 'Book a session',
                  href: '/book',
                },
                {
                  title: 'Document Support',
                  copy: 'Help filling forms and organizing requirements. Educational support only.',
                  price: 'Starting at €3 per page',
                  cta: 'Request document support',
                  href: '/book',
                },
                {
                  title: 'Speaking & Workshops',
                  copy: 'Invite Sista Kyerewaa to speak at your event or community program.',
                  price: 'Request pricing',
                  cta: 'Invite me to speak',
                  href: '/book',
                },
              ].map((service) => (
                <div key={service.title} className="border border-[#8C7B6B]/20 bg-white/70 p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B]">{service.price}</p>
                  <h3 className="font-display text-2xl text-[#2E4036] mt-3 mb-2">{service.title}</h3>
                  <p className="text-[#4A4541]">{service.copy}</p>
                  <a
                    href={service.href}
                    className="inline-flex items-center justify-center mt-6 px-6 py-3 border border-[#8C7B6B] text-xs uppercase tracking-[0.25em] text-[#2E4036] hover:bg-[#ede9df] transition-colors"
                  >
                    {service.cta}
                  </a>
                </div>
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#8C7B6B] text-center mt-6">
              Educational support only. For legal or medical advice, consult a licensed professional.{' '}
              <a href="/faq" className="underline text-[#2E4036]">Read the FAQ</a>.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
